import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save } from "lucide-react";

interface Interview {
  id: string;
  candidate_id: string;
  interview_type: string;
  scheduled_date: string;
  status: string;
  interview_data?: any;
}

interface InterviewConductorProps {
  interview: Interview;
  candidateName: string;
  onCompleted: () => void;
  onCancel: () => void;
}

export function InterviewConductor({ 
  interview, 
  candidateName, 
  onCompleted, 
  onCancel 
}: InterviewConductorProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [evaluationData, setEvaluationData] = useState({
    evaluation_score: '',
    evaluation_notes: '',
    recommendation: '',
    // Criteria scores (1-10)
    communication_skills: '',
    technical_knowledge: '',
    experience_relevance: '',
    professionalism: '',
    problem_solving: '',
    physical_fitness: '',
    reliability_assessment: '',
    overall_impression: '',
    strengths: '',
    weaknesses: '',
    additional_notes: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setEvaluationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Update interview status
      const { error: interviewError } = await supabase
        .from('interviews')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          evaluation_score: evaluationData.evaluation_score ? parseInt(evaluationData.evaluation_score) : null,
          evaluation_notes: evaluationData.evaluation_notes,
          recommendation: evaluationData.recommendation,
        })
        .eq('id', interview.id);

      if (interviewError) throw interviewError;

      // Create evaluation record
      const { error: evaluationError } = await supabase
        .from('candidate_evaluations')
        .insert({
          candidate_id: interview.candidate_id,
          interview_id: interview.id,
          evaluation_type: interview.interview_type,
          evaluator_id: (await supabase.auth.getUser()).data.user?.id,
          communication_skills: evaluationData.communication_skills ? parseInt(evaluationData.communication_skills) : null,
          technical_knowledge: evaluationData.technical_knowledge ? parseInt(evaluationData.technical_knowledge) : null,
          experience_relevance: evaluationData.experience_relevance ? parseInt(evaluationData.experience_relevance) : null,
          professionalism: evaluationData.professionalism ? parseInt(evaluationData.professionalism) : null,
          problem_solving: evaluationData.problem_solving ? parseInt(evaluationData.problem_solving) : null,
          physical_fitness: evaluationData.physical_fitness ? parseInt(evaluationData.physical_fitness) : null,
          reliability_assessment: evaluationData.reliability_assessment ? parseInt(evaluationData.reliability_assessment) : null,
          overall_impression: evaluationData.overall_impression ? parseInt(evaluationData.overall_impression) : null,
          strengths: evaluationData.strengths,
          weaknesses: evaluationData.weaknesses,
          additional_notes: evaluationData.additional_notes
        });

      if (evaluationError) throw evaluationError;

      toast({
        title: "Επιτυχία",
        description: "Η συνέντευξη ολοκληρώθηκε και τα αποτελέσματα καταγράφηκαν",
      });

      onCompleted();
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = evaluationData.evaluation_score && evaluationData.recommendation;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Διεξαγωγή Συνέντευξης - {candidateName}
          </CardTitle>
          <Button variant="outline" onClick={onCancel}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Επιστροφή
          </Button>
        </div>
        <p className="text-muted-foreground">
          {interview.interview_type === 'first' ? 'Πρώτη Συνέντευξη' : 'Δεύτερη Συνέντευξη'} - 
          {new Date(interview.scheduled_date).toLocaleDateString('el-GR')}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Evaluation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="evaluation_score">Συνολική Βαθμολογία (1-10)</Label>
            <Input
              id="evaluation_score"
              type="number"
              min="1"
              max="10"
              value={evaluationData.evaluation_score}
              onChange={(e) => handleInputChange('evaluation_score', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="recommendation">Σύσταση</Label>
            <Select value={evaluationData.recommendation} onValueChange={(value) => handleInputChange('recommendation', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Επιλέξτε σύσταση" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hire">Πρόσληψη</SelectItem>
                <SelectItem value="second_interview">Δεύτερη Συνέντευξη</SelectItem>
                <SelectItem value="reject">Απόρριψη</SelectItem>
                <SelectItem value="hold">Αναμονή</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Detailed Criteria */}
        <div>
          <h3 className="text-lg font-medium mb-4">Λεπτομερή Κριτήρια Αξιολόγησης (1-10)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="communication_skills">Επικοινωνιακές Δεξιότητες</Label>
              <Input
                id="communication_skills"
                type="number"
                min="1"
                max="10"
                value={evaluationData.communication_skills}
                onChange={(e) => handleInputChange('communication_skills', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="technical_knowledge">Τεχνική Γνώση</Label>
              <Input
                id="technical_knowledge"
                type="number"
                min="1"
                max="10"
                value={evaluationData.technical_knowledge}
                onChange={(e) => handleInputChange('technical_knowledge', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="experience_relevance">Σχετική Εμπειρία</Label>
              <Input
                id="experience_relevance"
                type="number"
                min="1"
                max="10"
                value={evaluationData.experience_relevance}
                onChange={(e) => handleInputChange('experience_relevance', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="professionalism">Επαγγελματισμός</Label>
              <Input
                id="professionalism"
                type="number"
                min="1"
                max="10"
                value={evaluationData.professionalism}
                onChange={(e) => handleInputChange('professionalism', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="problem_solving">Επίλυση Προβλημάτων</Label>
              <Input
                id="problem_solving"
                type="number"
                min="1"
                max="10"
                value={evaluationData.problem_solving}
                onChange={(e) => handleInputChange('problem_solving', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="physical_fitness">Φυσική Κατάσταση</Label>
              <Input
                id="physical_fitness"
                type="number"
                min="1"
                max="10"
                value={evaluationData.physical_fitness}
                onChange={(e) => handleInputChange('physical_fitness', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="reliability_assessment">Αξιοπιστία</Label>
              <Input
                id="reliability_assessment"
                type="number"
                min="1"
                max="10"
                value={evaluationData.reliability_assessment}
                onChange={(e) => handleInputChange('reliability_assessment', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="overall_impression">Συνολική Εντύπωση</Label>
              <Input
                id="overall_impression"
                type="number"
                min="1"
                max="10"
                value={evaluationData.overall_impression}
                onChange={(e) => handleInputChange('overall_impression', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Qualitative Assessment */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="strengths">Δυνατά Σημεία</Label>
            <Textarea
              id="strengths"
              value={evaluationData.strengths}
              onChange={(e) => handleInputChange('strengths', e.target.value)}
              placeholder="Περιγράψτε τα δυνατά σημεία του υποψηφίου..."
            />
          </div>
          <div>
            <Label htmlFor="weaknesses">Αδύνατα Σημεία</Label>
            <Textarea
              id="weaknesses"
              value={evaluationData.weaknesses}
              onChange={(e) => handleInputChange('weaknesses', e.target.value)}
              placeholder="Περιγράψτε τα αδύνατα σημεία του υποψηφίου..."
            />
          </div>
          <div>
            <Label htmlFor="evaluation_notes">Σημειώσεις Αξιολόγησης</Label>
            <Textarea
              id="evaluation_notes"
              value={evaluationData.evaluation_notes}
              onChange={(e) => handleInputChange('evaluation_notes', e.target.value)}
              placeholder="Γενικές σημειώσεις για τη συνέντευξη..."
            />
          </div>
          <div>
            <Label htmlFor="additional_notes">Επιπλέον Σημειώσεις</Label>
            <Textarea
              id="additional_notes"
              value={evaluationData.additional_notes}
              onChange={(e) => handleInputChange('additional_notes', e.target.value)}
              placeholder="Οποιεσδήποτε επιπλέον παρατηρήσεις..."
            />
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onCancel}>
            Ακύρωση
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!isFormValid || loading}
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Αποθήκευση...' : 'Ολοκλήρωση Συνέντευξης'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}